import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";

import { Tooltip } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiArrowNarrowRight } from "react-icons/hi";

import { TypographyButton } from "../../../../components/SignUp/styled";
import { useAppState } from "../../../../context/StateContext";
import { useNotificationHook } from "../../../../hooks/useNotificationActions";
import { Notification, NotificationUpdateAction } from "../../../../models/notification";
import NotificationsService from "../../../../services/notifications.service";
import { NotificationAction } from "../../../../util/enums";
import {
  MarkAllAsRead,
  MarkAllAsReadIcon,
  MarkAllAsReadLoading,
  NotificationsHeaderItem,
  NotificationsMenuBadge,
  NotificationsMenuFooter,
  NotificationsMenuHeader,
  NotificationsMenuHeaderTitle,
  NotificationsMenuListContainer,
  NotificationsMenuListBottom,
  NotificationsMenuListSpinner,
  SeeAllNotifications,
  NotificationsMenuListEnd,
} from "../styles";
import { EmptyNotificationList } from "./EmptyNotificationList";
import NotificationListItem from "./NotificationListItem";
import SkeletonNotifications from "./SkeletonNotifications";

interface NotificationsListDropdownProps {
  handleClose: () => void;
  handleGoToNotificationsScreen: () => void;
  handleSelected: () => void;
}

const NotificationsListDropdown = (
  { handleClose, handleGoToNotificationsScreen, handleSelected }: NotificationsListDropdownProps,
  _ref: React.Ref<HTMLElement>
) => {
  const { t: translate } = useTranslation();
  const { setUnreadTotal } = useAppState();
  const {
    importantTotal,
    notificationAction,
    setNotificationAction,

    getImportantNotificationCount,
    refetchUnreadNotificationsCount,
    useReadAllNotifications,

    useUpdateNotification,
  } = useNotificationHook();

  const { mutate: updateNotification, isLoading: isLoadingNotificationUpdate } =
    useUpdateNotification();

  const [notificationsDropdownData, setNotificationsDropdownData] = useState<Notification[]>([]);

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingNotificationsDropdown,
    data,
  } = useInfiniteQuery(
    ["notification.list.last.three"],
    ({ pageParam = 0 }) =>
      NotificationsService.getAllMy({
        page: pageParam,
        pageSize: 3,
        sortBy: JSON.stringify([["read"], ["updatedAt", "desc"]]),
        include: JSON.stringify([
          { association: "fromUser", include: [{ association: "picture" }] },
        ]),
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.data.length ? allPages.length : undefined;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const { mutate: handleReadAllNotifications, isLoading: isLoadingReadAllNotifications } =
    useReadAllNotifications();

  getImportantNotificationCount();

  useEffect(() => {
    refetchUnreadNotificationsCount();
  }, []);

  const notificationsDropdownList = useMemo(() => {
    return data?.pages
      .map((page) =>
        page.data.map((notification) => {
          return notification;
        })
      )
      .flat();
  }, [data]);

  useEffect(() => {
    setNotificationsDropdownData(notificationsDropdownList || []);
  }, [notificationsDropdownList]);

  const lastNotificationIntersectionObserver = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useCallback(
    (notificationListItemComponent: HTMLDivElement) => {
      if (isFetchingNextPage) return;

      if (lastNotificationIntersectionObserver.current) {
        lastNotificationIntersectionObserver.current.disconnect();
      }

      lastNotificationIntersectionObserver.current = new IntersectionObserver((notifications) => {
        if (notifications[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (notificationListItemComponent) {
        lastNotificationIntersectionObserver.current.observe(notificationListItemComponent);
      }
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage]
  );

  const handleSeeAllNotifications = () => {
    handleGoToNotificationsScreen();
    handleClose();
  };

  const changeMarkAsImportant = (notificationId?: number) => {
    if (!notificationId) return null;

    const notificationsData = notificationsDropdownData ? [...notificationsDropdownData] : [];

    const findNotification = notificationsData.find(
      (notification) => Number(notification.id) === Number(notificationId)
    );

    const index = notificationsData.findIndex((notification) => notification.id === notificationId);

    if (index || index === 0) {
      notificationsData[index].important = !notificationsData[index].important;
    }

    if (findNotification) {
      setNotificationAction({
        ids: [notificationId],
        action: NotificationAction.IMPORTANT_FROM_DROPDOWN,
      });

      updateNotification({
        id: Number(notificationId),
        notification: {
          ...findNotification,
          important: notificationsData[index].important,
        },
        action: NotificationAction.IMPORTANT_FROM_DROPDOWN,
      });
    }
  };

  const changeMarkAsRead = (notificationId?: number) => {
    if (!notificationId) return null;

    const notificationsData = notificationsDropdownData ? [...notificationsDropdownData] : [];

    const findNotification = notificationsData.find(
      (notification) => Number(notification.id) === Number(notificationId)
    );

    const index = notificationsData.findIndex((notification) => notification.id === notificationId);

    if ((index || index === 0) && !notificationsData[index].read) {
      notificationsData[index].read = true;

      if (findNotification) {
        setNotificationAction({
          ids: [notificationId],
          action: NotificationAction.READ_FROM_DROPDOWN,
        });

        updateNotification({
          id: Number(notificationId),
          notification: {
            ...findNotification,
            read: notificationsData[index].read,
          },
          action: NotificationAction.READ_FROM_DROPDOWN,
        });
      }
    }
  };

  const handleReadAllMyNotifications = () => {
    setUnreadTotal(0);
    handleReadAllNotifications();
    const notificationsData = notificationsDropdownData ? [...notificationsDropdownData] : [];

    if (notificationsData) {
      notificationsData.forEach((_, index) => {
        notificationsData[index].read = true;
      });
    }

    setNotificationAction({
      ids: notificationsData
        .map((notification) => notification.id)
        .filter(Boolean) as NotificationUpdateAction["ids"],
      action: NotificationAction.ALL_READ_FROM_DROPDOWN,
    });
  };

  const renderLoadingNotification = () => {
    return (
      <NotificationsMenuListBottom container direction="row">
        <NotificationsMenuListSpinner size={24} />
      </NotificationsMenuListBottom>
    );
  };

  const renderNotificationsEnd = () => {
    return (
      <NotificationsMenuListBottom container direction="row">
        <NotificationsMenuListEnd>
          {translate("youHaveReachedTheEndOfNotifications")}
        </NotificationsMenuListEnd>
        🎉
      </NotificationsMenuListBottom>
    );
  };

  return (
    <>
      <NotificationsMenuHeader container data-cy="userNotificationsDropdown">
        <NotificationsHeaderItem>
          <NotificationsMenuHeaderTitle>{translate("notifications")}</NotificationsMenuHeaderTitle>
          {importantTotal > 0 && (
            <Tooltip
              title={`${importantTotal} ${translate("importantNotifications")}`}
              placement="top"
            >
              <div>
                <NotificationsMenuBadge>{importantTotal}</NotificationsMenuBadge>
              </div>
            </Tooltip>
          )}
        </NotificationsHeaderItem>
        {isLoadingReadAllNotifications ? (
          <MarkAllAsReadLoading size={16} />
        ) : (
          <>
            {notificationsDropdownData?.length ? (
              <NotificationsHeaderItem
                as={TypographyButton}
                onClick={() => handleReadAllMyNotifications()}
              >
                <MarkAllAsRead>{translate("markAllAsRead")}</MarkAllAsRead>
                <MarkAllAsReadIcon />
              </NotificationsHeaderItem>
            ) : null}
          </>
        )}
      </NotificationsMenuHeader>
      <NotificationsMenuListContainer container>
        {isLoadingNotificationsDropdown ? (
          <>
            <SkeletonNotifications />
            <SkeletonNotifications />
            <SkeletonNotifications />
          </>
        ) : (
          notificationsDropdownData &&
          notificationsDropdownData.length > 0 &&
          notificationsDropdownData?.map((notification, index) => {
            if (notificationsDropdownData.length === index + 1) {
              return (
                <NotificationListItem
                  key={notification.id}
                  notification={notification}
                  lastNotificationRef={lastNotificationRef}
                  notificationAction={notificationAction}
                  isLoadingNotificationUpdate={isLoadingNotificationUpdate}
                  changeMarkAsImportant={() => changeMarkAsImportant(notification.id)}
                  changeMarkAsRead={() => changeMarkAsRead(notification.id)}
                  close={() => handleClose()}
                  handleSelected={handleSelected}
                />
              );
            }

            return (
              <NotificationListItem
                key={notification.id}
                notification={notification}
                notificationAction={notificationAction}
                isLoadingNotificationUpdate={isLoadingNotificationUpdate}
                changeMarkAsImportant={() => changeMarkAsImportant(notification.id)}
                changeMarkAsRead={() => changeMarkAsRead(notification.id)}
                close={() => handleClose()}
                handleSelected={handleSelected}
              />
            );
          })
        )}
        {isFetchingNextPage && renderLoadingNotification()}
        {notificationsDropdownData?.length > 2 && !hasNextPage && renderNotificationsEnd()}
        {!isLoadingNotificationsDropdown &&
          !isFetchingNextPage &&
          !notificationsDropdownData?.length && <EmptyNotificationList />}
      </NotificationsMenuListContainer>
      <NotificationsMenuFooter container>
        <NotificationsHeaderItem
          as={TypographyButton}
          onClick={handleSeeAllNotifications}
          data-cy="seeAllNotifications"
        >
          <SeeAllNotifications>{translate("seeAllNotifications")}</SeeAllNotifications>
          <HiArrowNarrowRight size={16} color="#465869" />
        </NotificationsHeaderItem>
      </NotificationsMenuFooter>
    </>
  );
};

export default React.forwardRef(NotificationsListDropdown);
